import { useMemo, memo } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import {
  FaHome,
  FaBuilding,
  FaBolt,
  FaChartLine,
  FaCog,
  FaLeaf,
  FaServer,
  FaShieldAlt,
  FaHospital,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const FeatureIcon = memo(({ icon: Icon }) => (
  <div className="relative group">
    <div className="absolute inset-0 bg-blue-500/20 blur-lg group-hover:bg-blue-500/30 transition-all duration-300" />
    <div className="relative p-4 bg-gray-800 rounded-xl border border-gray-700 shadow-md group-hover:shadow-lg transition-all duration-300">
      <Icon className="w-7 h-7 text-blue-400" />
    </div>
  </div>
));

FeatureIcon.displayName = "FeatureIcon";

FeatureIcon.propTypes = {
  icon: PropTypes.elementType.isRequired,
};

const mainIconMapping = {
  FaHome: FaHome,
  FaBuilding: FaBuilding,
  FaHospital: FaHospital,
};

const featureIconMapping = {
  FaBolt: FaBolt,
  FaServer: FaServer,
  FaLeaf: FaLeaf,
  FaChartLine: FaChartLine,
  FaCog: FaCog,
  FaShieldAlt: FaShieldAlt,
};

const FeatureCard = memo(({ feature, idx }) => {
  const FeatureIconComponent = featureIconMapping[feature.icon];

  return (
    <motion.div
      key={idx}
      whileHover={{ x: 5 }}
      className="p-4 bg-gray-700 rounded-xl border border-gray-600 hover:shadow-md transition-all duration-300 w-full"
    >
      <div className="flex items-center gap-4">
        <div className="shrink-0">
          <FeatureIcon icon={FeatureIconComponent} />
        </div>
        <div className="flex flex-col justify-between flex-1">
          <h4 className="text-sm font-bold text-gray-300 mb-1">
            {feature.title}
          </h4>
          <p className="text-md font-bold text-blue-400">{feature.value}</p>
        </div>
      </div>
    </motion.div>
  );
});

FeatureCard.displayName = "FeatureCard";

FeatureCard.propTypes = {
  feature: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  idx: PropTypes.number.isRequired,
};

const SolutionCard = memo(({ solution, index }) => {
  const MainIcon = mainIconMapping[solution.icon];

  return (
    <motion.div
      key={index}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.2 }}
      className="group h-full"
    >
      <div className="relative p-8 rounded-2xl bg-gray-800 backdrop-blur-xl border border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-500 h-full flex flex-col">
        <div className="relative flex flex-col h-full">
          <div className="flex items-start gap-6 mb-8">
            <div
              className={`p-4 rounded-xl bg-linear-to-r ${solution.accent} shadow-lg group-hover:shadow-xl transition-all duration-300 shrink-0`}
            >
              {MainIcon && <MainIcon className="w-8 h-8 text-white" />}
            </div>
            <div className="min-h-[80px] flex flex-col">
              <h3 className="text-md md:text-xl font-bold text-gray-100 mb-2">
                {solution.title}
              </h3>
              <p className="text-sm text-gray-400">{solution.description}</p>
            </div>
          </div>

          <div className="flex flex-col gap-4 mt-auto">
            {solution.features.map((feature, idx) => (
              <FeatureCard key={idx} feature={feature} idx={idx} />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
});

SolutionCard.displayName = "SolutionCard";

SolutionCard.propTypes = {
  solution: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    accent: PropTypes.string.isRequired,
    features: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
};

const SolutionBreakdown = () => {
  const { t } = useTranslation();
  const data = useMemo(
    () => t("components.solution.solutionBreakdown", { returnObjects: true }),
    [t]
  );
  const { sectionTitle, sectionDescription, solutions } = data;

  return (
    <div
      className="py-16 px-4 relative overflow-hidden bg-black text-gray-200"
      id="safePad"
    >
      <div className="absolute inset-0 opacity-30">
        <div
          className="h-full w-full"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(156, 163, 175, 0.1) 1px, transparent 0)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold mb-6 text-gray-100">
            {sectionTitle}
          </h2>
          <div className="w-32 h-1.5 bg-linear-to-r from-blue-500 to-green-500 mx-auto mb-8 rounded-full" />
          <p className="text-md lg:text-lg text-gray-400 max-w-3xl mx-auto min-h-[60px]">
            {sectionDescription}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {solutions.map((solution, index) => (
            <SolutionCard key={index} solution={solution} index={index} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(SolutionBreakdown);
